import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiDollarSign, FiChevronDown, FiChevronUp, FiRefreshCw, FiAlertCircle, FiDownload, FiFileText, FiBarChart2, FiSave, FiClock, FiLock } from 'react-icons/fi';
import { CostBreakdownItem, UnsupportedResource, exportCostPDF, exportCostExcel, saveCostEstimation as saveEstimation } from '../../api/cost';
import { formatCost } from '../../hooks/useCostEstimation';
import { CostAnalysisDrawer } from './CostAnalysisDrawer';
import { usePlanFeatures } from '../../hooks/usePlanFeatures';
import { isFeatureLevelSufficient } from '../../types/features';

interface CostEstimationPanelProps {
  totalMonthly: number;
  totalHourly: number;
  currency: string;
  breakdown: CostBreakdownItem[];
  unsupported: UnsupportedResource[];
  loading: boolean;
  error: string | null;
  updatedAt?: string;
  disclaimer?: string;
  onRefresh?: () => void;
  resources?: any[]; // Pour les exports
  projectName?: string;
}

/**
 * Panel flottant d'estimation des coûts
 * Affiché en bas à droite du canvas
 */
export const CostEstimationPanel: React.FC<CostEstimationPanelProps> = ({
  totalMonthly,
  totalHourly,
  currency,
  breakdown,
  unsupported,
  loading,
  error,
  updatedAt,
  disclaimer,
  onRefresh,
  resources = [],
  projectName = 'Architecture Cloud'
}) => {
  const { t } = useTranslation('canvas');
  const { hasFeature, getFeatureLevel } = usePlanFeatures();
  const exportLevel = getFeatureLevel('cost_estimation');
  const canExport = hasFeature('cost_estimation') &&
    exportLevel !== null &&
    typeof exportLevel === 'string' &&
    isFeatureLevelSufficient(exportLevel, 'standard');

  const [isExpanded, setIsExpanded] = useState(false);
  const [showUnsupported, setShowUnsupported] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const [showAnalysisDrawer, setShowAnalysisDrawer] = useState(false);
  const [saving, setSaving] = useState(false);
  const [budget, setBudget] = useState<number | null>(null);
  const [showBudgetInput, setShowBudgetInput] = useState(false);
  const [showForecasts, setShowForecasts] = useState(false);

  // Draggable state
  const [position, setPosition] = useState({ x: window.innerWidth - 320, y: window.innerHeight - 300 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return; // Don't drag when clicking buttons
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    const newX = Math.max(0, Math.min(window.innerWidth - 300, e.clientX - dragStart.x));
    const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - dragStart.y));
    setPosition({ x: newX, y: newY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  const handleExportPDF = async () => {
    try {
      setExporting('pdf');
      await exportCostPDF(resources, {
        projectName,
        companyName: 'GenIaC',
        currency
      });
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert(t('cost.messages.exportPDFError'));
    } finally {
      setExporting(null);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting('excel');
      await exportCostExcel(resources, {
        projectName,
        currency
      });
    } catch (error) {
      console.error('Erreur lors de l\'export Excel:', error);
      alert(t('cost.messages.exportExcelError'));
    } finally {
      setExporting(null);
    }
  };

  const handleSaveEstimation = async () => {
    try {
      setSaving(true);
      await saveEstimation({
        projectName,
        totalMonthly,
        totalHourly,
        currency,
        breakdown,
        resources,
        unsupported,
        budget: budget || undefined,
      });
      alert(t('cost.messages.savingSuccess'));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert(t('cost.messages.savingError'));
    } finally {
      setSaving(false);
    }
  };

  const getForecasts = () => {
    return {
      quarterly: totalMonthly * 3,
      semiAnnual: totalMonthly * 6,
      annual: totalMonthly * 12,
    };
  };

  const getBudgetUsage = () => {
    if (!budget || budget === 0) return null;
    return (totalMonthly / budget) * 100;
  };

  const budgetUsage = getBudgetUsage();
  const isOverBudget = budgetUsage !== null && budgetUsage >= 80;
  const hasResources = breakdown.length > 0 || unsupported.length > 0;

  return (
    <div
      onMouseDown={handleMouseDown}
      style={{
        position: 'fixed',
        left: `${position.x}px`,
        top: `${position.y}px`,
        backgroundColor: '#1e293b',
        border: '1px solid #334155',
        borderRadius: '8px',
        boxShadow: isDragging ? '0 8px 24px rgba(0, 0, 0, 0.5)' : '0 4px 12px rgba(0, 0, 0, 0.3)',
        minWidth: '280px',
        maxWidth: '400px',
        zIndex: 1000,
        color: '#e2e8f0',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        cursor: isDragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        transition: isDragging ? 'none' : 'box-shadow 0.2s'
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: isExpanded ? '1px solid #334155' : 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiDollarSign size={20} style={{ color: '#10b981' }} />
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{t('cost.title')}</span>
          {/* Indicateur de refresh discret - seulement si pas en loading principal */}
          {!loading && (breakdown.length > 0 || unsupported.length > 0) && (
            <div
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#10b981',
                opacity: 0.6,
                animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
              }}
              title={t('cost.autoUpdateTooltip')}
            />
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {loading && (
            <FiRefreshCw
              size={16}
              style={{ color: '#60a5fa', animation: 'spin 1s linear infinite' }}
            />
          )}
          {isExpanded ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
        </div>
      </div>

      {/* Total Cost */}
      <div style={{ padding: '16px' }}>
        {error ? (
          <div style={{ display: 'flex', alignItems: 'start', gap: '8px', color: '#f87171' }}>
            <FiAlertCircle size={18} style={{ marginTop: '2px', flexShrink: 0 }} />
            <span style={{ fontSize: '13px' }}>{error}</span>
          </div>
        ) : !hasResources ? (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>
            {t('cost.empty')}
          </div>
        ) : (
          <>
            <div style={{ marginBottom: '8px' }}>
              <div style={{ fontSize: '24px', fontWeight: 700, color: '#10b981' }}>
                {formatCost(totalMonthly, currency)}<span style={{ fontSize: '14px', fontWeight: 400, color: '#94a3b8' }}>{t('cost.perMonth')}</span>
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                {formatCost(totalHourly, currency)}{t('cost.perHour')}
              </div>
            </div>

            {breakdown.length > 0 && (
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '8px' }}>
                {t('cost.resourcesEstimated', { count: breakdown.length })}
              </div>
            )}

            {unsupported.length > 0 && (
              <div style={{ fontSize: '12px', color: '#fbbf24', marginTop: '4px' }}>
                {t('cost.resourcesUnsupported', { count: unsupported.length })}
              </div>
            )}
          </>
        )}

        {/* Analyse Détaillée Button */}
        {hasResources && !loading && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowAnalysisDrawer(true);
            }}
            style={{
              marginTop: '12px',
              width: '100%',
              padding: '8px 12px',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              border: 'none',
              borderRadius: '4px',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <FiBarChart2 size={14} />
            {t('cost.buttons.detailedAnalysis')}
          </button>
        )}

        {onRefresh && !loading && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRefresh();
            }}
            style={{
              marginTop: '8px',
              width: '100%',
              padding: '6px 12px',
              backgroundColor: '#334155',
              border: '1px solid #475569',
              borderRadius: '4px',
              color: '#e2e8f0',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <FiRefreshCw size={14} />
            {t('cost.buttons.refresh')}
          </button>
        )}

        {/* Save Button */}
        {hasResources && !loading && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleSaveEstimation();
            }}
            disabled={saving}
            style={{
              marginTop: '8px',
              width: '100%',
              padding: '6px 12px',
              backgroundColor: '#10b981',
              border: 'none',
              borderRadius: '4px',
              color: '#ffffff',
              fontSize: '12px',
              fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              opacity: saving ? 0.6 : 1
            }}
          >
            {saving ? <FiRefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <FiSave size={14} />}
            {saving ? t('cost.buttons.saving') : t('cost.buttons.save')}
          </button>
        )}

        {/* Budget Alert */}
        {budget && isOverBudget && (
          <div style={{
            marginTop: '8px',
            padding: '8px',
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#991b1b'
          }}>
            <FiAlertCircle size={12} style={{ marginRight: '4px', display: 'inline' }} />
            {t('cost.budgetAlert', { percent: budgetUsage?.toFixed(0) })}
          </div>
        )}

        {/* Forecasts Toggle */}
        {hasResources && !loading && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowForecasts(!showForecasts);
            }}
            style={{
              marginTop: '8px',
              width: '100%',
              padding: '6px 12px',
              backgroundColor: 'transparent',
              border: '1px solid #475569',
              borderRadius: '4px',
              color: '#e2e8f0',
              fontSize: '11px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <FiClock size={12} />
            {showForecasts ? t('cost.buttons.hideForecasts') : t('cost.buttons.showForecasts')}
          </button>
        )}

        {/* Forecasts Display */}
        {showForecasts && hasResources && (
          <div style={{
            marginTop: '8px',
            padding: '8px',
            backgroundColor: '#0f172a',
            borderRadius: '4px',
            fontSize: '11px'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', color: '#cbd5e1' }}>
              {t('cost.forecasts.title')}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#94a3b8' }}>{t('cost.forecasts.quarterly')}</span>
              <span style={{ color: '#10b981', fontWeight: 600 }}>{formatCost(getForecasts().quarterly, currency)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: '#94a3b8' }}>{t('cost.forecasts.semiAnnual')}</span>
              <span style={{ color: '#10b981', fontWeight: 600 }}>{formatCost(getForecasts().semiAnnual, currency)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#94a3b8' }}>{t('cost.forecasts.annual')}</span>
              <span style={{ color: '#10b981', fontWeight: 600 }}>{formatCost(getForecasts().annual, currency)}</span>
            </div>
          </div>
        )}

        {/* Export Buttons */}
        {hasResources && !loading && resources.length > 0 && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!canExport) { window.location.href = '/settings/subscription'; return; }
                handleExportPDF();
              }}
              disabled={exporting === 'pdf'}
              title={canExport ? undefined : 'Export PDF — Plan Standard requis'}
              style={{
                flex: 1,
                padding: '6px 12px',
                backgroundColor: canExport ? '#dc2626' : '#475569',
                border: canExport ? 'none' : '1px dashed #64748b',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '11px',
                cursor: exporting === 'pdf' ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                opacity: exporting === 'pdf' ? 0.6 : 1
              }}
            >
              {!canExport
                ? <FiLock size={12} />
                : exporting === 'pdf'
                  ? <FiRefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  : <FiFileText size={12} />}
              {t('cost.buttons.exportPDF')}
              {!canExport && <span style={{ fontSize: '9px', opacity: 0.7, marginLeft: 2 }}>STANDARD</span>}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!canExport) { window.location.href = '/settings/subscription'; return; }
                handleExportExcel();
              }}
              disabled={exporting === 'excel'}
              title={canExport ? undefined : 'Export Excel — Plan Standard requis'}
              style={{
                flex: 1,
                padding: '6px 12px',
                backgroundColor: canExport ? '#059669' : '#475569',
                border: canExport ? 'none' : '1px dashed #64748b',
                borderRadius: '4px',
                color: '#fff',
                fontSize: '11px',
                cursor: exporting === 'excel' ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                opacity: exporting === 'excel' ? 0.6 : 1
              }}
            >
              {!canExport
                ? <FiLock size={12} />
                : exporting === 'excel'
                  ? <FiRefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} />
                  : <FiDownload size={12} />}
              {t('cost.buttons.exportExcel')}
              {!canExport && <span style={{ fontSize: '9px', opacity: 0.7, marginLeft: 2 }}>STANDARD</span>}
            </button>
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && hasResources && (
        <div
          style={{
            borderTop: '1px solid #334155',
            maxHeight: '400px',
            overflowY: 'auto'
          }}
        >
          {/* Breakdown */}
          {breakdown.length > 0 && (
            <div style={{ padding: '12px 16px' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: '#cbd5e1' }}>
                {t('cost.breakdown.title')}
              </div>
              {breakdown.map((item) => (
                <div
                  key={item.resourceId}
                  style={{
                    padding: '8px',
                    backgroundColor: '#0f172a',
                    borderRadius: '4px',
                    marginBottom: '6px',
                    fontSize: '12px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 500, color: '#e2e8f0' }}>{item.name}</span>
                    <span style={{ color: '#10b981', fontWeight: 600 }}>
                      {formatCost(item.monthly, currency)}/mois
                    </span>
                  </div>
                  <div style={{ color: '#64748b', fontSize: '11px' }}>
                    {item.sku} • {item.region}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Unsupported Resources */}
          {unsupported.length > 0 && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid #334155' }}>
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  marginBottom: '8px',
                  color: '#fbbf24',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                onClick={() => setShowUnsupported(!showUnsupported)}
              >
                <FiAlertCircle size={14} />
                {t('cost.breakdown.unsupported', { count: unsupported.length })}
                {showUnsupported ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              </div>

              {showUnsupported && (
                <div style={{ marginTop: '8px' }}>
                  {unsupported.map((item) => (
                    <div
                      key={item.resourceId}
                      style={{
                        padding: '8px',
                        backgroundColor: '#422006',
                        borderRadius: '4px',
                        marginBottom: '6px',
                        fontSize: '11px',
                        color: '#fde68a'
                      }}
                    >
                      <div style={{ fontWeight: 500, marginBottom: '2px' }}>
                        {item.resourceType}
                      </div>
                      <div style={{ opacity: 0.8 }}>
                        {item.error}: {item.detail}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Disclaimer */}
          {disclaimer && (
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #334155',
                fontSize: '11px',
                color: '#64748b',
                fontStyle: 'italic'
              }}
            >
              {disclaimer}
            </div>
          )}

          {/* Updated At */}
          {updatedAt && (
            <div
              style={{
                padding: '8px 16px',
                borderTop: '1px solid #334155',
                fontSize: '10px',
                color: '#475569',
                textAlign: 'center'
              }}
            >
              {t('cost.updatedAt', { date: new Date(updatedAt).toLocaleString('fr-FR') })}
            </div>
          )}
        </div>
      )}

      {/* Animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 0.6;
              transform: scale(1);
            }
            50% {
              opacity: 1;
              transform: scale(1.2);
            }
          }
        `}
      </style>

      {/* Analysis Drawer */}
      <CostAnalysisDrawer
        isOpen={showAnalysisDrawer}
        onClose={() => setShowAnalysisDrawer(false)}
        totalMonthly={totalMonthly}
        totalHourly={totalHourly}
        currency={currency}
        breakdown={breakdown}
        unsupported={unsupported}
        resources={resources}
      />
    </div>
  );
};
